(() => {
  const script = document.currentScript;
  const measurementId = script?.dataset.measurementId || '';
  const configuredPageType = script?.dataset.pageType || '';
  const experimentGroup = script?.dataset.experimentGroup || 'not_assigned';
  const pageStartedAt = performance.now();
  const states = new Map();
  let scanScheduled = false;

  const providers = {
    klaviyo: [
      '[role="dialog"][aria-label="POPUP Form"]',
      '[role="dialog"][data-kl-scroll-locking-modal="true"]',
      '#klaviyo-bis-iframe',
    ],
    ecomsend: [
      '#ecomsend-widget .react-responsive-modal-overlay',
      '#ecomsend-widget .pp-overlay',
      '#ecomsend-widget .spinWheelWrapper.dialogVisible',
    ],
    location: ['.header-town-selector-modal'],
    whatsapp: ['#es-whatsapp-review-prompt-root.es-review-prompt-visible'],
    pushowl: ['[role="dialog"][id*="pushowl" i]', '[role="dialog"][class*="pushowl" i]'],
    tolstoy: ['[role="dialog"][id*="tolstoy" i]', '[role="dialog"][class*="tolstoy" i]', 'tolstoy-modal'],
  };

  const closeSelectors = [
    '[aria-label*="close" i]',
    '[aria-label*="cerrar" i]',
    '[data-testid*="close" i]',
    '[class*="close" i]',
    '.cerrar',
    '.mas-tarde',
  ].join(',');

  function sendEvent(name, parameters = {}) {
    const payload = {
      ...parameters,
      send_to: measurementId || undefined,
      transport_type: 'beacon',
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  }

  function pageType() {
    if (/^\/cart(?:\/|$)/.test(window.location.pathname)) return 'cart';
    return configuredPageType || 'other';
  }

  function deviceType() {
    if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
    return 'desktop';
  }

  function purchaseContext() {
    if (/^\/cart(?:\/|$)/.test(window.location.pathname)) return 'cart_page';
    if (document.querySelector('cart-drawer.active')) return 'cart_drawer';
    return 'none';
  }

  function isVisible(element) {
    if (!element?.isConnected) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function isBlocking(element, provider = '') {
    if (!isVisible(element)) return false;

    if (provider === 'location') {
      const locationOverlay = document.querySelector('.location-overlay.active');
      if (isVisible(locationOverlay) && window.getComputedStyle(locationOverlay).pointerEvents !== 'none') return true;
    }

    const style = window.getComputedStyle(element);
    if (style.pointerEvents === 'none') return false;

    const rect = element.getBoundingClientRect();
    const viewportArea = Math.max(window.innerWidth * window.innerHeight, 1);
    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const coverage = (visibleWidth * visibleHeight) / viewportArea;

    return (
      element.getAttribute('aria-modal') === 'true' ||
      element.hasAttribute('data-kl-scroll-locking-modal') ||
      element.matches('.pp-overlay, .spinWheelWrapper.dialogVisible') ||
      coverage >= 0.5
    );
  }

  function safeIdentifier(value) {
    if (!value) return '';
    const normalized = String(value).trim().slice(0, 80);
    return /^[a-zA-Z0-9_.:-]+$/.test(normalized) ? normalized : '';
  }

  function popupId(element, provider) {
    const candidates = [
      element.dataset?.popupId,
      element.dataset?.formId,
      element.getAttribute('data-testid'),
      element.id,
      element.querySelector?.('[data-popup-id]')?.getAttribute('data-popup-id'),
      element.querySelector?.('[data-form-id]')?.getAttribute('data-form-id'),
    ];

    return candidates.map(safeIdentifier).find(Boolean) || `${provider}_unknown`;
  }

  function eventContext(state) {
    const context = purchaseContext();
    const blocking = state.visible ? isBlocking(state.element, state.provider) : state.blocking;
    return {
      popup_provider: state.provider,
      popup_id: state.popupId,
      page_type: pageType(),
      device_type: deviceType(),
      cart_open: context === 'cart_drawer' ? 1 : 0,
      purchase_context: context,
      popup_blocking: blocking ? 1 : 0,
      experiment_group: experimentGroup,
      event_category: 'popup',
      event_label: `${state.provider}:${state.popupId}`,
    };
  }

  function findCandidates() {
    const candidates = new Map();
    Object.entries(providers).forEach(([provider, selectors]) => {
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (!candidates.has(element)) candidates.set(element, provider);
        });
      });
    });
    return candidates;
  }

  function stateFor(element, provider) {
    let state = states.get(element);
    if (!state) {
      state = {
        element,
        provider,
        popupId: popupId(element, provider),
        visible: false,
        shownAt: 0,
        closeMethod: 'unknown',
        blocking: false,
        obstructionContexts: new Set(),
        visibleErrors: new Set(),
      };
      states.set(element, state);
    }
    return state;
  }

  function recordObstruction(state) {
    const context = purchaseContext();
    if (
      context === 'none' ||
      !isBlocking(state.element, state.provider) ||
      state.obstructionContexts.has(context)
    )
      return;
    state.obstructionContexts.add(context);
    sendEvent('popup_obstruction', {
      ...eventContext(state),
      obstruction_context: context,
    });
  }

  function visibleErrors(state) {
    return Array.from(
      state.element.querySelectorAll('[role="alert"], .error_message, [class*="error-message" i], [class*="form-error" i]')
    ).filter(isVisible);
  }

  function scan() {
    const candidates = findCandidates();

    candidates.forEach((provider, element) => {
      const state = stateFor(element, provider);
      const visible = isVisible(element);

      if (visible && !state.visible) {
        state.visible = true;
        state.shownAt = performance.now();
        state.closeMethod = 'unknown';
        state.blocking = isBlocking(element, state.provider);
        state.obstructionContexts.clear();
        state.visibleErrors.clear();
        sendEvent('popup_view', {
          ...eventContext(state),
          seconds_to_popup: Number(((state.shownAt - pageStartedAt) / 1000).toFixed(1)),
        });
      }

      if (visible) {
        state.blocking = state.blocking || isBlocking(element, state.provider);
        recordObstruction(state);
        visibleErrors(state).forEach((errorElement) => {
          const errorKey = safeIdentifier(errorElement.id || errorElement.getAttribute('data-testid')) || 'visible_error';
          if (state.visibleErrors.has(errorKey)) return;
          state.visibleErrors.add(errorKey);
          sendEvent('popup_error', { ...eventContext(state), popup_error_type: errorKey });
        });
      }

      if (!visible && state.visible) {
        state.visible = false;
        sendEvent('popup_close', {
          ...eventContext(state),
          close_method: state.closeMethod,
          popup_visible_seconds: Number(((performance.now() - state.shownAt) / 1000).toFixed(1)),
        });
      }
    });

    states.forEach((state, element) => {
      if (candidates.has(element) || !state.visible) return;
      state.visible = false;
      sendEvent('popup_close', {
        ...eventContext(state),
        close_method: state.closeMethod,
        popup_visible_seconds: Number(((performance.now() - state.shownAt) / 1000).toFixed(1)),
      });
      if (!element.isConnected) states.delete(element);
    });
  }

  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    window.requestAnimationFrame(() => {
      scanScheduled = false;
      scan();
    });
  }

  function stateContaining(target) {
    if (!(target instanceof Element)) return null;
    return Array.from(states.values()).find((state) => state.visible && state.element.contains(target)) || null;
  }

  function markCloseMethod(state, target) {
    if (!state || !(target instanceof Element)) return false;
    if (target.closest(closeSelectors)) {
      state.closeMethod = 'button';
      return true;
    }
    if (target === state.element && state.element.matches('.pp-overlay, .spinWheelWrapper.dialogVisible')) {
      state.closeMethod = 'outside';
      return true;
    }
    return false;
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (target instanceof Element && target.closest('.location-overlay.active')) {
        const locationState = Array.from(states.values()).find(
          (state) => state.visible && state.provider === 'location'
        );
        if (locationState) locationState.closeMethod = 'outside';
        return;
      }

      const state = stateContaining(target);
      if (!state || markCloseMethod(state, target)) return;
      if (target instanceof Element && target.closest('input, select, textarea, label')) return;

      sendEvent('popup_click', {
        ...eventContext(state),
        popup_action: target instanceof Element && target.closest('a, button') ? 'cta' : 'content',
      });

      const copyControl =
        target instanceof Element && target.closest('[data-copy], [class*="copy" i], [aria-label*="copiar" i]');
      const controlText = target instanceof Element ? target.closest('button, a')?.textContent || '' : '';
      if (copyControl || /copiar|copy|c[oó]digo|cup[oó]n/i.test(controlText)) {
        sendEvent('popup_discount_copy', eventContext(state));
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const state = stateContaining(event.target);
      if (state) sendEvent('popup_submit', eventContext(state));
    },
    true
  );

  document.addEventListener(
    'copy',
    (event) => {
      const state = stateContaining(event.target);
      if (state) sendEvent('popup_discount_copy', eventContext(state));
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    states.forEach((state) => {
      if (state.visible) state.closeMethod = 'escape';
    });
  });

  document.addEventListener('gotta:popup-error', (event) => {
    const detail = event.detail || {};
    const provider = safeIdentifier(detail.popup_provider) || 'unknown';
    sendEvent('popup_error', {
      popup_provider: provider,
      popup_id: safeIdentifier(detail.popup_id) || `${provider}_unknown`,
      popup_error_type: safeIdentifier(detail.popup_error_type) || 'integration_error',
      page_type: pageType(),
      device_type: deviceType(),
      purchase_context: purchaseContext(),
      experiment_group: experimentGroup,
      event_category: 'popup',
      event_label: provider,
    });
  });

  function start() {
    scan();
    new MutationObserver(scheduleScan).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'style'],
    });
    window.addEventListener('resize', scheduleScan, { passive: true });
  }

  window.GottaPopupAnalytics = {
    scan: scheduleScan,
    providers: Object.keys(providers),
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
